import React, { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  ClipboardUtil,
  IconButton,
  TextareaContentUtil,
  Tooltip,
} from '@axiom/ui';

import type { ChatAnswerSegment } from '../../types/chat-answer.types';
import { ChatAnswerCopyUtil } from '../../utils/chat-answer-copy-util';

const CONFIRMED_MS = 1600;

// A plain copy waits this long so the first click of a double click does not clobber the formatted copy.
const DOUBLE_CLICK_MS = 250;

enum Copied {
  HTML = 'html',
  NONE = 'none',
  RICH = 'rich',
  TEXT = 'text',
}

const TOOLTIP_LABEL: Record<Copied, string> = {
  [Copied.HTML]: 'HTML copied',
  [Copied.NONE]: 'Copy',
  [Copied.RICH]: 'Copied with formatting',
  [Copied.TEXT]: 'Copied',
};

export type ChatCopyButtonProps = {
  /** Left off, `markdown` is copied as it stands - for a button over several regions. */
  contentRef?: React.RefObject<HTMLDivElement | null>;
  /** Copy formatted on a single click, rather than on the double click it otherwise waits for. */
  formattedOnClick?: boolean;
  /** Names the action in the tooltip - "Copy body", "Copy subject". Defaults to "Copy". */
  label?: string;
  markdown: string;
  /** Defaults to the answer-level button, which is the only unnamed one. */
  name?: string;
  segments?: ChatAnswerSegment[];
};

export const ChatCopyButton = ({
  contentRef,
  formattedOnClick = false,
  label = TOOLTIP_LABEL[Copied.NONE],
  markdown,
  name = 'COPY_ANSWER',
  segments,
}: ChatCopyButtonProps) => {
  const [copied, setCopied] = useState(Copied.NONE);

  const [copyCount, setCopyCount] = useState(0);

  const confirm = (state: Copied) => {
    setCopied(state);
    setCopyCount(previous => previous + 1);
  };

  const [hasEditor, setHasEditor] = useState(false);

  const copyTextRef = useRef<() => void>(() => {});

  useEffect(() => {
    setHasEditor(
      TextareaContentUtil.hasEditor({ container: contentRef?.current ?? null })
    );
  }, [contentRef, markdown]);

  useEffect(() => {
    if (copied === Copied.NONE) return;

    const timer = window.setTimeout(() => {
      setCopied(Copied.NONE);
    }, CONFIRMED_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [copied, copyCount]);

  const readText = () => {
    const container = contentRef?.current ?? null;

    return segments
      ? ChatAnswerCopyUtil.toText({ container, segments })
      : TextareaContentUtil.toText({ container });
  };

  const readHtml = () => {
    const container = contentRef?.current ?? null;

    return segments
      ? ChatAnswerCopyUtil.toHtml({ container, segments })
      : TextareaContentUtil.toHtml({ container });
  };

  const copyText = () => {
    void ClipboardUtil.copy({
      text: readText() ?? markdown,
    }).then(() => {
      confirm(Copied.TEXT);
    });
  };

  const copyFromEditor = ({
    state,
    toEntry,
  }: {
    state: Copied;
    toEntry: (html: string) => { html?: string | null; text: string };
  }) => {
    const html = readHtml();

    if (!html) return;

    void ClipboardUtil.copy(toEntry(html)).then(() => {
      confirm(state);
    });
  };

  const copyRich = () => {
    copyFromEditor({
      state: Copied.RICH,
      toEntry: html => ({ html, text: markdown }),
    });
  };

  const copyMarkup = () => {
    copyFromEditor({
      state: Copied.HTML,
      // The markup itself, as plain text - no html flavour, or the target renders it.
      toEntry: html => ({ text: html }),
    });
  };

  // Stable debounce calling through a ref: a rebuilt one would leave the first click's timer running.
  const copyTextSoon = useMemo(
    () =>
      debounce(() => {
        copyTextRef.current();
      }, DOUBLE_CLICK_MS),
    []
  );

  useEffect(() => {
    copyTextRef.current = copyText;
  });

  useEffect(() => copyTextSoon.cancel, [copyTextSoon]);

  const click = (event: React.MouseEvent) => {
    copyTextSoon.cancel();

    if (event.shiftKey && hasEditor) {
      copyMarkup();
      return;
    }

    if (formattedOnClick && hasEditor) {
      copyRich();
      return;
    }

    copyTextSoon();
  };

  const doubleClick = (event: React.MouseEvent) => {
    copyTextSoon.cancel();

    // Each click of a shift double click already copied the markup.
    if (event.shiftKey) return;

    if (formattedOnClick && hasEditor) return;

    if (hasEditor) {
      copyRich();
      return;
    }

    copyText();
  };

  const glyph = copied === Copied.NONE ? 'copy' : 'checkmark';

  return (
    <Tooltip
      anchor={
        <IconButton
          icon={glyph}
          name={name}
          onClick={click}
          onDoubleClick={doubleClick}
          pattern="secondary"
          size="small"
          variation="minimal"
        />
      }
      fade
      hideClose
      name={`${name}_EXPLANATION`}
      position="top"
      variation="minimal"
    >
      {copied === Copied.NONE ? label : TOOLTIP_LABEL[copied]}
    </Tooltip>
  );
};
